import React,{useState} from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import {useAuth0Data} from '../../../context/Auth0DataContext';

const {FiUsers,FiPlus,FiEdit,FiTrash2,FiSave,FiX,FiCalendar,FiCopy} = FiIcons;

const SalesTeamManagement = () => {
  const {
    data,
    addItem,
    updateItem,
    deleteItem,
    salesTeamYear,
    salesTeamAvailableYears,
    setSalesTeamYear,
    initializeSalesTeamYear
  } = useAuth0Data();
  const [isAdding,setIsAdding] = useState(false);
  const [editingId,setEditingId] = useState(null);
  const [isInitializing,setIsInitializing] = useState(false);
  const [formData,setFormData] = useState({
    name: '',
    role: 'business-development-manager',
    gmCurrent: 0,
    gmGoal: 0,
    meetings: 0,
    meetingsGoal: 0,
    msas: 0,
    msasGoal: 0,
    jobs: 0,
    jobsGoal: 0,
    // Client Coordinator specific fields
    qtdPlacements: 0,
    qtdPlacementsGoal: 0,
    qtdIntake: 0,
    qtdIntakeGoal: 0,
    ytdPlacements: 0,
    ytdPlacementsGoal: 0,
    ytdIntake: 0,
    ytdIntakeGoal: 0
  });

  const roles = [
    {value: 'business-development-manager',label: 'Business Development Manager'},
    {value: 'business-development-director',label: 'Business Development Director'},
    {value: 'client-coordinator',label: 'Client Coordinator'}
  ];

  const handleAdd = () => {
    if (formData.name.trim()) {
      addItem('salesTeam',{...formData,year: salesTeamYear});
      setFormData({
        name: '',
        role: 'business-development-manager',
        gmCurrent: 0,
        gmGoal: 0,
        meetings: 0,
        meetingsGoal: 0,
        msas: 0,
        msasGoal: 0,
        jobs: 0,
        jobsGoal: 0,
        qtdPlacements: 0,
        qtdPlacementsGoal: 0,
        qtdIntake: 0,
        qtdIntakeGoal: 0,
        ytdPlacements: 0,
        ytdPlacementsGoal: 0,
        ytdIntake: 0,
        ytdIntakeGoal: 0
      });
      setIsAdding(false);
    }
  };

  const handleEdit = (member) => {
    setEditingId(member.id);
    setFormData({
      name: member.name,
      role: member.role || 'business-development-manager',
      gmCurrent: member.gmCurrent || 0,
      gmGoal: member.gmGoal || 0,
      meetings: member.meetings || 0,
      meetingsGoal: member.meetingsGoal || 0,
      msas: member.msas || 0,
      msasGoal: member.msasGoal || 0,
      jobs: member.jobs || 0,
      jobsGoal: member.jobsGoal || 0,
      qtdPlacements: member.qtdPlacements || 0,
      qtdPlacementsGoal: member.qtdPlacementsGoal || 0,
      qtdIntake: member.qtdIntake || 0,
      qtdIntakeGoal: member.qtdIntakeGoal || 0,
      ytdPlacements: member.ytdPlacements || 0,
      ytdPlacementsGoal: member.ytdPlacementsGoal || 0,
      ytdIntake: member.ytdIntake || 0,
      ytdIntakeGoal: member.ytdIntakeGoal || 0
    });
  };

  const handleUpdate = () => {
    updateItem('salesTeam',editingId,formData);
    setEditingId(null);
    setFormData({
      name: '',
      role: 'business-development-manager',
      gmCurrent: 0,
      gmGoal: 0,
      meetings: 0,
      meetingsGoal: 0,
      msas: 0,
      msasGoal: 0,
      jobs: 0,
      jobsGoal: 0,
      qtdPlacements: 0,
      qtdPlacementsGoal: 0,
      qtdIntake: 0,
      qtdIntakeGoal: 0,
      ytdPlacements: 0,
      ytdPlacementsGoal: 0,
      ytdIntake: 0,
      ytdIntakeGoal: 0
    });
  };

  const handleCancel = () => {
    setIsAdding(false);
    setEditingId(null);
    setFormData({
      name: '',
      role: 'business-development-manager',
      gmCurrent: 0,
      gmGoal: 0,
      meetings: 0,
      meetingsGoal: 0,
      msas: 0,
      msasGoal: 0,
      jobs: 0,
      jobsGoal: 0,
      qtdPlacements: 0,
      qtdPlacementsGoal: 0,
      qtdIntake: 0,
      qtdIntakeGoal: 0,
      ytdPlacements: 0,
      ytdPlacementsGoal: 0,
      ytdIntake: 0,
      ytdIntakeGoal: 0
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US',{
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getRoleLabel = (role) => {
    const roleObj = roles.find(r => r.value === role);
    return roleObj ? roleObj.label : role;
  };

  const isClientCoordinator = (role) => role === 'client-coordinator';
  const isBusinessDevelopmentDirector = (role) => role === 'business-development-director';

  const handleYearChange = async (newYear) => {
    await setSalesTeamYear(newYear);
  };

  const handleInitializeYear = async () => {
    const previousYear = salesTeamYear - 1;
    const confirmMessage = `Initialize ${salesTeamYear} by copying team members from ${previousYear}?\n\nThis will create new records for ${salesTeamYear} with the same goals but reset metrics to 0.`;
    if (confirm(confirmMessage)) {
      setIsInitializing(true);
      try {
        await initializeSalesTeamYear(salesTeamYear);
        alert(`Successfully initialized ${salesTeamYear} sales team data!`);
      } catch (error) {
        alert(`Error: ${error.message}`);
      } finally {
        setIsInitializing(false);
      }
    }
  };

  // Generate year options (current year + next year, plus any years with data)
  const currentYear = new Date().getFullYear();
  const yearOptions = [...new Set([...salesTeamAvailableYears, currentYear, currentYear + 1])].sort((a, b) => b - a);

  const renderBusinessDevelopmentDirectorFields = () => {
    if (!isBusinessDevelopmentDirector(formData.role)) return null;

    return (
      <>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">GM Current</label>
          <input
            type="number"
            value={formData.gmCurrent}
            onChange={(e) => setFormData(prev => ({...prev,gmCurrent: parseInt(e.target.value) || 0}))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">GM Goal</label>
          <input
            type="number"
            value={formData.gmGoal}
            onChange={(e) => setFormData(prev => ({...prev,gmGoal: parseInt(e.target.value) || 0}))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">MSAs Current</label>
          <input
            type="number"
            value={formData.msas}
            onChange={(e) => setFormData(prev => ({...prev,msas: parseInt(e.target.value) || 0}))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">MSAs Goal</label>
          <input
            type="number"
            value={formData.msasGoal}
            onChange={(e) => setFormData(prev => ({...prev,msasGoal: parseInt(e.target.value) || 0}))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
      </>
    );
  };

  const renderBusinessDevelopmentManagerFields = () => {
    if (isClientCoordinator(formData.role) || isBusinessDevelopmentDirector(formData.role)) return null;

    return (
      <>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">GM Current</label>
          <input
            type="number"
            value={formData.gmCurrent}
            onChange={(e) => setFormData(prev => ({...prev,gmCurrent: parseInt(e.target.value) || 0}))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">GM Goal</label>
          <input
            type="number"
            value={formData.gmGoal}
            onChange={(e) => setFormData(prev => ({...prev,gmGoal: parseInt(e.target.value) || 0}))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Meetings</label>
          <input
            type="number"
            value={formData.meetings}
            onChange={(e) => setFormData(prev => ({...prev,meetings: parseInt(e.target.value) || 0}))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Meetings Goal</label>
          <input
            type="number"
            value={formData.meetingsGoal}
            onChange={(e) => setFormData(prev => ({...prev,meetingsGoal: parseInt(e.target.value) || 0}))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">MSAs</label>
          <input
            type="number"
            value={formData.msas}
            onChange={(e) => setFormData(prev => ({...prev,msas: parseInt(e.target.value) || 0}))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">MSAs Goal</label>
          <input
            type="number"
            value={formData.msasGoal}
            onChange={(e) => setFormData(prev => ({...prev,msasGoal: parseInt(e.target.value) || 0}))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Jobs</label>
          <input
            type="number"
            value={formData.jobs}
            onChange={(e) => setFormData(prev => ({...prev,jobs: parseInt(e.target.value) || 0}))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Jobs Goal</label>
          <input
            type="number"
            value={formData.jobsGoal}
            onChange={(e) => setFormData(prev => ({...prev,jobsGoal: parseInt(e.target.value) || 0}))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
      </>
    );
  };

  const renderCoordinatorFields = () => {
    if (!isClientCoordinator(formData.role)) return null;

    return (
      <>
        <div className="md:col-span-3">
          <h5 className="text-md font-semibold text-gray-900 mb-3">Quarterly Performance</h5>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">QTD Placements</label>
          <input
            type="number"
            value={formData.qtdPlacements}
            onChange={(e) => setFormData(prev => ({...prev,qtdPlacements: parseInt(e.target.value) || 0}))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">QTD Placements Goal</label>
          <input
            type="number"
            value={formData.qtdPlacementsGoal}
            onChange={(e) => setFormData(prev => ({...prev,qtdPlacementsGoal: parseInt(e.target.value) || 0}))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">QTD Intake Calls</label>
          <input
            type="number"
            value={formData.qtdIntake}
            onChange={(e) => setFormData(prev => ({...prev,qtdIntake: parseInt(e.target.value) || 0}))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">QTD Intake Goal</label>
          <input
            type="number"
            value={formData.qtdIntakeGoal}
            onChange={(e) => setFormData(prev => ({...prev,qtdIntakeGoal: parseInt(e.target.value) || 0}))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <div className="md:col-span-3">
          <h5 className="text-md font-semibold text-gray-900 mb-3 mt-4">Year-to-Date Performance</h5>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">YTD Placements</label>
          <input
            type="number"
            value={formData.ytdPlacements}
            onChange={(e) => setFormData(prev => ({...prev,ytdPlacements: parseInt(e.target.value) || 0}))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">YTD Placements Goal</label>
          <input
            type="number"
            value={formData.ytdPlacementsGoal}
            onChange={(e) => setFormData(prev => ({...prev,ytdPlacementsGoal: parseInt(e.target.value) || 0}))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">YTD Intake Calls</label>
          <input
            type="number"
            value={formData.ytdIntake}
            onChange={(e) => setFormData(prev => ({...prev,ytdIntake: parseInt(e.target.value) || 0}))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">YTD Intake Goal</label>
          <input
            type="number"
            value={formData.ytdIntakeGoal}
            onChange={(e) => setFormData(prev => ({...prev,ytdIntakeGoal: parseInt(e.target.value) || 0}))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
      </>
    );
  };

  return (
    <motion.div
      initial={{opacity: 0,y: 20}}
      animate={{opacity: 1,y: 0}}
      className="bg-white rounded-xl shadow-soft border border-gray-100 p-6"
    >
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
            <SafeIcon icon={FiUsers} className="w-5 h-5 text-blue-600" />
          </div>
          <h3 className="ml-3 text-xl font-bold text-gray-900">Sales Team Management</h3>
        </div>
        <div className="flex items-center gap-3">
          {/* Year Selector */}
          <div className="flex items-center gap-2">
            <SafeIcon icon={FiCalendar} className="w-4 h-4 text-gray-500" />
            <select
              value={salesTeamYear}
              onChange={(e) => handleYearChange(parseInt(e.target.value))}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-medium"
            >
              {yearOptions.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
          <button
            onClick={() => setIsAdding(true)}
            className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <SafeIcon icon={FiPlus} className="w-4 h-4 mr-2" />
            Add Team Member
          </button>
        </div>
      </div>

      {/* Add Form */}
      {isAdding && (
        <motion.div
          initial={{opacity: 0,height: 0}}
          animate={{opacity: 1,height: 'auto'}}
          className="mb-6 p-4 bg-gray-50 rounded-lg"
        >
          <h4 className="font-semibold text-gray-900 mb-4">Add New Team Member</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({...prev,name: e.target.value}))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Team member name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
              <select
                value={formData.role}
                onChange={(e) => setFormData(prev => ({...prev,role: e.target.value}))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                {roles.map(role => (
                  <option key={role.value} value={role.value}>{role.label}</option>
                ))}
              </select>
            </div>
            <div></div> {/* Empty div for grid spacing */}
            {renderBusinessDevelopmentManagerFields()}
            {renderBusinessDevelopmentDirectorFields()}
            {renderCoordinatorFields()}
          </div>
          <div className="flex gap-2 mt-4">
            <button
              onClick={handleAdd}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <SafeIcon icon={FiSave} className="w-4 h-4 mr-2" />
              Save
            </button>
            <button
              onClick={handleCancel}
              className="flex items-center px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
            >
              <SafeIcon icon={FiX} className="w-4 h-4 mr-2" />
              Cancel
            </button>
          </div>
        </motion.div>
      )}

      {/* Team Members List */}
      <div className="space-y-6">
        {data.salesTeam.map((member,index) => (
          <motion.div
            key={member.id}
            initial={{opacity: 0,y: 10}}
            animate={{opacity: 1,y: 0}}
            transition={{delay: index * 0.1}}
            className="border border-gray-200 rounded-lg p-6"
          >
            {editingId === member.id ? (
              <div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({...prev,name: e.target.value}))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                    <select
                      value={formData.role}
                      onChange={(e) => setFormData(prev => ({...prev,role: e.target.value}))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      {roles.map(role => (
                        <option key={role.value} value={role.value}>{role.label}</option>
                      ))}
                    </select>
                  </div>
                  <div></div> {/* Empty div for grid spacing */}
                  {renderBusinessDevelopmentManagerFields()}
                  {renderBusinessDevelopmentDirectorFields()}
                  {renderCoordinatorFields()}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleUpdate}
                    className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <SafeIcon icon={FiSave} className="w-4 h-4 mr-2" />
                    Save
                  </button>
                  <button
                    onClick={handleCancel}
                    className="flex items-center px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
                  >
                    <SafeIcon icon={FiX} className="w-4 h-4 mr-2" />
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900">{member.name}</h4>
                    <p className="text-sm text-gray-600">{getRoleLabel(member.role)}</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEdit(member)}
                      className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                    >
                      <SafeIcon icon={FiEdit} className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => deleteItem('salesTeam',member.id)}
                      className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                    >
                      <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Display fields based on role */}
                {isClientCoordinator(member.role) ? (
                  <div className="space-y-4">
                    <div>
                      <h5 className="font-medium text-gray-900 mb-3">Quarterly Performance</h5>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="text-center p-3 bg-blue-50 rounded-lg">
                          <p className="text-sm text-gray-600">QTD Placements</p>
                          <p className="font-medium text-gray-900">{member.qtdPlacements || 0}</p>
                          <p className="text-xs text-gray-500">Goal: {member.qtdPlacementsGoal || 0}</p>
                        </div>
                        <div className="text-center p-3 bg-green-50 rounded-lg">
                          <p className="text-sm text-gray-600">QTD Intake</p>
                          <p className="font-medium text-gray-900">{member.qtdIntake || 0}</p>
                          <p className="text-xs text-gray-500">Goal: {member.qtdIntakeGoal || 0}</p>
                        </div>
                      </div>
                    </div>
                    <div>
                      <h5 className="font-medium text-gray-900 mb-3">Year-to-Date Performance</h5>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="text-center p-3 bg-purple-50 rounded-lg">
                          <p className="text-sm text-gray-600">YTD Placements</p>
                          <p className="font-medium text-gray-900">{member.ytdPlacements || 0}</p>
                          <p className="text-xs text-gray-500">Goal: {member.ytdPlacementsGoal || 0}</p>
                        </div>
                        <div className="text-center p-3 bg-orange-50 rounded-lg">
                          <p className="text-sm text-gray-600">YTD Intake</p>
                          <p className="font-medium text-gray-900">{member.ytdIntake || 0}</p>
                          <p className="text-xs text-gray-500">Goal: {member.ytdIntakeGoal || 0}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : isBusinessDevelopmentDirector(member.role) ? (
                  // Simplified view for Business Development Director - only GM and MSAs
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center p-3 bg-green-50 rounded-lg">
                      <p className="text-sm text-gray-600">Gross Margin</p>
                      <p className="font-medium text-gray-900">{formatCurrency(member.gmCurrent || 0)}</p>
                      <p className="text-xs text-gray-500">Goal: {formatCurrency(member.gmGoal || 0)}</p>
                    </div>
                    <div className="text-center p-3 bg-purple-50 rounded-lg">
                      <p className="text-sm text-gray-600">MSAs</p>
                      <p className="font-medium text-gray-900">{member.msas || 0}</p>
                      <p className="text-xs text-gray-500">Goal: {member.msasGoal || 0}</p>
                    </div>
                  </div>
                ) : (
                  // Full view for Business Development Manager
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center p-3 bg-green-50 rounded-lg">
                      <p className="text-sm text-gray-600">Gross Margin</p>
                      <p className="font-medium text-gray-900">{formatCurrency(member.gmCurrent || 0)}</p>
                      <p className="text-xs text-gray-500">Goal: {formatCurrency(member.gmGoal || 0)}</p>
                    </div>
                    <div className="text-center p-3 bg-blue-50 rounded-lg">
                      <p className="text-sm text-gray-600">Meetings</p>
                      <p className="font-medium text-gray-900">{member.meetings || 0}</p>
                      <p className="text-xs text-gray-500">Goal: {member.meetingsGoal || 0}</p>
                    </div>
                    <div className="text-center p-3 bg-purple-50 rounded-lg">
                      <p className="text-sm text-gray-600">MSAs</p>
                      <p className="font-medium text-gray-900">{member.msas || 0}</p>
                      <p className="text-xs text-gray-500">Goal: {member.msasGoal || 0}</p>
                    </div>
                    <div className="text-center p-3 bg-orange-50 rounded-lg">
                      <p className="text-sm text-gray-600">Jobs</p>
                      <p className="font-medium text-gray-900">{member.jobs || 0}</p>
                      <p className="text-xs text-gray-500">Goal: {member.jobsGoal || 0}</p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </motion.div>
        ))}
      </div>

      {data.salesTeam.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <SafeIcon icon={FiUsers} className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p className="mb-4">No team members found for {salesTeamYear}.</p>
          {salesTeamAvailableYears.includes(salesTeamYear - 1) && (
            <button
              onClick={handleInitializeYear}
              disabled={isInitializing}
              className="flex items-center mx-auto px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400"
            >
              <SafeIcon icon={FiCopy} className="w-4 h-4 mr-2" />
              {isInitializing ? 'Initializing...' : `Initialize ${salesTeamYear} from ${salesTeamYear - 1}`}
            </button>
          )}
          {!salesTeamAvailableYears.includes(salesTeamYear - 1) && (
            <p className="text-sm">Add team members using the button above.</p>
          )}
        </div>
      )}
    </motion.div>
  );
};

export default SalesTeamManagement;